import { useEffect } from "react";
import { toast } from "sonner";
import { detectBrowserLanguage } from "@/lib/ui-translations";

// A client's questionnaire is a single-page app loaded once. A dossier can stay
// open in the same tab for weeks (one Blouin file was started on June 1st), so a
// fix deployed in the meantime never reaches that tab: the client keeps seeing
// the bug we shipped a fix for. On every return to the tab (throttled), compare
// the bundle the server now serves with the one running here; when they differ,
// offer a reload. Answers are saved as they are typed and the position is
// persisted, so reloading loses nothing.
const CHECK_EVERY_MS = 5 * 60 * 1000;
const COPY: Record<string, { title: string; body: string; action: string }> = {
  fr: { title: "Nouvelle version disponible", body: "Vos réponses sont enregistrées. Rechargez la page pour continuer avec la version à jour.", action: "Recharger" },
  en: { title: "New version available", body: "Your answers are saved. Reload the page to continue with the up-to-date version.", action: "Reload" },
  es: { title: "Nueva versión disponible", body: "Sus respuestas están guardadas. Recargue la página para continuar con la versión actualizada.", action: "Recargar" },
};

const runningBundle = (): string | null => {
  const s = document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]');
  return s ? s.getAttribute("src") : null;
};

async function servedBundle(): Promise<string | null> {
  try {
    const res = await fetch(`${window.location.origin}/?v=${Date.now()}`, { cache: "no-store", headers: { Accept: "text/html" } });
    if (!res.ok) return null;
    const html = await res.text();
    const m = html.match(/<script[^>]+type="module"[^>]+src="([^"]*\/assets\/index-[^"]+)"/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

export function useNewVersionWatch(): void {
  useEffect(() => {
    if (import.meta.env.DEV) return; // Vite dev serves no hashed bundle
    const current = runningBundle();
    if (!current) return;
    let lastCheck = 0;
    let announced = false;
    const check = async () => {
      if (announced || Date.now() - lastCheck < CHECK_EVERY_MS) return;
      lastCheck = Date.now();
      const served = await servedBundle();
      if (!served || served === current) return;
      announced = true;
      const c = COPY[detectBrowserLanguage()] ?? COPY.fr;
      toast(c.title, {
        description: c.body,
        duration: Infinity,
        action: { label: c.action, onClick: () => window.location.reload() },
      });
    };
    const onVisible = () => { if (document.visibilityState === "visible") void check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    // First check a little after load, so a tab restored by the browser from
    // its cache (weeks old) is caught too.
    const t = window.setTimeout(() => void check(), 30_000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      window.clearTimeout(t);
    };
  }, []);
}
